import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Observable, of } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';

import { unformatCnpj } from '../utils/cnpj.util';

import { ApiService } from './api.service';

const STORAGE_KEY = 'umkt_utm_cnpj_has_account';

export interface UtmCnpjAccountStored {
  cnpj: string;
  hasAccount: boolean;
}

/**
 * Stores the result of GET /api/v1/user/cnpj/{cnpj} for UTM users.
 * When the user (from UTM) confirms location or modal opens with existing CNPJ,
 * we call the API and store whether that CNPJ has an account. On "finalizar pedido"
 * we redirect to /register (no account) or /login (has account).
 */
@Injectable({
  providedIn: 'root',
})
export class UtmCnpjAccountService {
  private memory: UtmCnpjAccountStored | null = null;
  private platformId = inject(PLATFORM_ID);
  private apiService = inject(ApiService);


  /**
   * Call GET /user/cnpj/{cnpj}, store result (true/false) and return it.
   * CNPJ must be 14 alphanumeric characters (unmasked, uppercase).
   */
  checkAndStoreCnpjHasAccount(cnpj: string): Observable<boolean> {
    const clean = unformatCnpj(cnpj);
    if (clean.length !== 14) {
      return of(false);
    }
    return this.apiService.get<boolean | { data?: boolean }>(`/user/cnpj/${encodeURIComponent(clean)}`).pipe(
      map((res): boolean => {
        if (typeof res === 'boolean') return res;
        if (res != null && typeof (res as { data?: boolean }).data === 'boolean') {
          return (res as { data: boolean }).data;
        }
        return false;
      }),
      catchError(() => of(false)),
      tap((hasAccount) => this.setStored(clean, hasAccount))
    );
  }

  /** Returns the stored hasAccount for the last checked CNPJ, or null if none. */
  getCnpjHasAccount(): boolean | null {
    const stored = this.getStored();
    return stored?.hasAccount ?? null;
  }

  /** Returns the stored CNPJ (14 digits) for the last check, or null if none. Use when redirecting to register to pre-fill. */
  getStoredCnpj(): string | null {
    const stored = this.getStored();
    return stored?.cnpj ?? null;
  }

  private getStored(): UtmCnpjAccountStored | null {
    if (this.memory) return this.memory;
    if (!isPlatformBrowser(this.platformId)) return null;
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as UtmCnpjAccountStored;
    } catch {
      return null;
    }
  }

  /** Clear stored value (e.g. when UTM is cleared or user logs in). */
  clear(): void {
    this.memory = null;
    if (isPlatformBrowser(this.platformId) && typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }

  private setStored(cnpj: string, hasAccount: boolean): void {
    const value: UtmCnpjAccountStored = { cnpj, hasAccount };
    this.memory = value;
    if (isPlatformBrowser(this.platformId) && typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    }
  }
}
